import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Megaphone, Bell, Calendar as CalendarIcon, LayoutDashboard, Plus, ChevronDown, Info, Clock, CheckCircle2, AlertOctagon } from "lucide-react";
import api, { formatDetail } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useAccess } from "@/lib/access";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import StickyContextBar from "@/components/StickyContextBar";
import AnnouncementsPage from "@/pages/AnnouncementsPage";
import RemindersPage from "@/pages/RemindersPage";
import { fmtDate } from "@/lib/constants";
import { toast } from "sonner";
import { computeReminderStatus, REMINDER_STATUS_LABEL } from "@/lib/reminderStatus";

/**
 * InfoAgendaPage — 149-bq. Unified shell yang menggabungkan Announcements +
 * Reminder & Event ke satu menu "Informasi & Agenda" dengan 4 tab:
 *   • Ringkasan  — KPI cards + feed gabungan (announcements + upcoming events)
 *   • Pengumuman — embed AnnouncementsPage existing (fungsi lama utuh)
 *   • Reminder & Event — embed RemindersPage existing (fungsi lama utuh)
 *   • Kalender   — embed RemindersPage; user pilih calendar sub-view di dalamnya
 *
 * Permission dipertahankan terpisah:
 *   - Tab Pengumuman muncul kalau `announcements.view`
 *   - Tab Reminder & Kalender muncul kalau `reminders.view`
 * Dropdown "+ Buat" hanya tampilkan aksi yg diizinkan (create perm).
 */
const TABS = [
  { id: "ringkasan",  label: "Ringkasan",       icon: LayoutDashboard, feature: null },
  { id: "pengumuman", label: "Pengumuman",      icon: Megaphone,       feature: "announcements" },
  { id: "reminder",   label: "Reminder & Event", icon: Bell,           feature: "reminders" },
  { id: "kalender",   label: "Kalender",        icon: CalendarIcon,    feature: "reminders" },
];

export default function InfoAgendaPage() {
  const { user } = useAuth();
  const { canPerm } = useAccess();
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // 149-cn (PROMPT 16-P0): announcements & reminders permission WAJIB explicit —
  // announcements TIDAK lagi grandfather ke `dashboard.view`.
  const canViewAnn = canPerm(user, "announcements", "view");
  const canViewRem = canPerm(user, "reminders", "view");
  const canCreateAnn = canPerm(user, "announcements", "create");
  const canCreateRem = canPerm(user, "reminders", "create");

  // 149-co (PROMPT 16-P0-R2): flag decoupling — di-render sebagai "Akses Ditolak"
  // penuh kalau kedua permission Announcements & Reminders OFF. Check dievaluasi
  // SETELAH semua hook supaya tidak melanggar rules-of-hooks.
  const denied = !canViewAnn && !canViewRem;

  const availableTabs = useMemo(() => TABS.filter((t) => {
    if (!t.feature) return true;
    if (t.feature === "announcements") return canViewAnn;
    if (t.feature === "reminders") return canViewRem;
    return true;
  }), [canViewAnn, canViewRem]);

  const initialTab = availableTabs.find((t) => t.id === searchParams.get("tab"))?.id || availableTabs[0]?.id || "ringkasan";
  const [tab, setTab] = useState(initialTab);
  useEffect(() => {
    const q = searchParams.get("tab");
    if (q && availableTabs.find((t) => t.id === q)) setTab(q);
  }, [searchParams, availableTabs]);
  const switchTab = (id) => {
    setTab(id);
    const next = new URLSearchParams(searchParams);
    next.set("tab", id);
    setSearchParams(next, { replace: true });
  };

  // ---- Ringkasan data ----
  const [announcements, setAnnouncements] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const loadRingkasan = useCallback(async () => {
    setLoading(true);
    try {
      const calls = [];
      if (canViewAnn) calls.push(api.get("/announcements").then((r) => setAnnouncements(r.data || [])).catch(() => setAnnouncements([])));
      if (canViewRem) {
        const from = new Date(); from.setDate(from.getDate() - 30);
        const to = new Date(); to.setDate(to.getDate() + 30);
        const params = { from: from.toISOString().slice(0, 10), to: to.toISOString().slice(0, 10) };
        calls.push(api.get("/reminders/events", { params }).then((r) => setEvents(r.data?.events || r.data || [])).catch(() => setEvents([])));
      }
      await Promise.all(calls);
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail) || "Gagal memuat ringkasan");
    } finally { setLoading(false); }
  }, [canViewAnn, canViewRem]);
  useEffect(() => { if (!denied && tab === "ringkasan") loadRingkasan(); }, [denied, tab, loadRingkasan]);

  // ---- KPI + Feed compute ----
  const kpi = useMemo(() => {
    const now = new Date();
    const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    const endOfDay = startOfDay + 86_400_000;
    const startOfWeek = startOfDay - ((now.getDay() + 6) % 7) * 86_400_000; // Monday
    const endOfWeek = startOfWeek + 7 * 86_400_000;
    let annActive = 0, hariIni = 0, mingguIni = 0, terlambat = 0, selesai = 0;
    // Announcements: active if within start/end range & !archived
    for (const a of announcements) {
      const start = a.start_date ? new Date(a.start_date).getTime() : 0;
      const end = a.end_date ? new Date(a.end_date).getTime() : Number.MAX_SAFE_INTEGER;
      const isActive = !a.archived && now.getTime() >= start && now.getTime() <= end;
      if (isActive) annActive += 1;
    }
    // 149-cq (PROMPT 16-R2A): status reminder pakai fungsi shared (Asia/Jakarta) —
    // konsisten antara halaman ini, Dashboard, feed, KPI, dan sisa aplikasi.
    for (const e of events) {
      const st = computeReminderStatus(e, now.getTime());
      const d = e.event_date ? new Date(String(e.event_date).slice(0, 10) + "T00:00:00").getTime() : 0;
      if (st === "done") selesai += 1;
      else if (st === "overdue") terlambat += 1;
      if (st !== "done" && st !== "cancelled") {
        if (d >= startOfDay && d < endOfDay) hariIni += 1;
        if (d >= startOfWeek && d < endOfWeek) mingguIni += 1;
      }
    }
    return { annActive, hariIni, mingguIni, terlambat, selesai };
  }, [announcements, events]);

  const feed = useMemo(() => {
    // Merge announcements + upcoming events into unified feed sorted by date desc
    const arr = [];
    for (const a of announcements) {
      arr.push({
        _kind: "PENGUMUMAN",
        _date: a.start_date || a.created_at,
        id: `ann-${a.id}`, title: a.title,
        category: a.category || "general",
        actor: a.creator_name || "—",
        status: a.archived ? "Diarsipkan" : "Aktif",
        raw: a,
      });
    }
    for (const e of events) {
      const type = e.reference?.type;
      let kind = "REMINDER";
      if (type === "asset") kind = "ASSET";
      else if (type === "project") kind = "PROJECT";
      else if (type === "tax_legal") kind = "PAJAK & LEGAL";
      // Heuristic: kalau ada `has_time` dan bukan reminder-only → EVENT
      if (e.has_time) kind = "EVENT";
      // 149-cq (PROMPT 16-R2A): status feed konsisten dengan Dashboard/badge lain.
      const st = computeReminderStatus(e);
      // 149-cr (PROMPT 16-R2A-R1): distinguisher — kalau reminder auto-generated
      // dari asset/project/tax_legal berbeda tetapi title sama, tambahkan
      // suffix identitas (Asset ID prefix, ref label) supaya user bisa membedakan
      // "Pajak Kendaraan: B 1234 TEST" milik 3 aset yang berbeda.
      const refLabel = e.reference?.label || (e.reference?.id ? `#${String(e.reference.id).slice(0, 6)}` : "");
      arr.push({
        _kind: kind,
        _date: e.event_date,
        id: `ev-${e.id}`,
        title: e.title,
        distinguisher: refLabel,
        category: e.category_name || e.category_id || "—",
        actor: (e.pic_names && e.pic_names.join(", ")) || e.creator_name || "—",
        status: REMINDER_STATUS_LABEL[st] || "Akan Datang",
        _status_key: st,
        raw: e,
      });
    }
    arr.sort((a, b) => (b._date || "").localeCompare(a._date || ""));
    return arr;
  }, [announcements, events]);

  const KIND_BADGE = {
    PENGUMUMAN:      "bg-orange-100 text-orange-800 border-orange-300",
    EVENT:           "bg-purple-100 text-purple-800 border-purple-300",
    REMINDER:        "bg-blue-100 text-blue-800 border-blue-300",
    ASSET:           "bg-teal-100 text-teal-800 border-teal-300",
    PROJECT:         "bg-sky-100 text-sky-800 border-sky-300",
    "PAJAK & LEGAL": "bg-rose-100 text-rose-800 border-rose-300",
  };

  return (
    <div className="p-6 space-y-4" data-testid="info-agenda-page">
      {/* 149-co PROMPT 16-P0-R2: Kalau kedua permission OFF → Akses Ditolak.
          TIDAK render tab, dropdown Buat, atau isi tab (loadRingkasan juga gated). */}
      {denied ? (
        <>
          <div>
            <p className="overline text-slate-500">Komunikasi & Agenda</p>
            <h1 className="font-display text-3xl tracking-tight text-[#0B1D3A]">Informasi & Agenda</h1>
          </div>
          <div className="border border-rose-200 bg-rose-50 p-8 text-center" data-testid="info-agenda-denied">
            <AlertOctagon size={28} className="mx-auto text-rose-500 mb-2" />
            <h2 className="font-display text-xl text-rose-900 tracking-tight">Akses Ditolak</h2>
            <p className="text-sm text-rose-700 mt-2 max-w-lg mx-auto">
              Anda tidak memiliki akses ke modul Pengumuman maupun Reminder &amp; Event. Hubungi admin
              untuk mengatur Access Control (Announcements &amp; Reminders → Lihat).
            </p>
          </div>
        </>
      ) : (<>
      {/* Page header. 149-cp (PROMPT 16-P0-R3): deskripsi & CTA adaptif berdasarkan
          permission yang dimiliki. Kalau hanya announcements, halaman TIDAK menjanjikan
          fitur reminder yang tidak dapat diakses. */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="overline text-slate-500">Komunikasi & Agenda</p>
          <h1 className="font-display text-3xl tracking-tight text-[#0B1D3A]">
            {canViewAnn && !canViewRem ? "Pengumuman" : (canViewRem && !canViewAnn ? "Reminder & Event" : "Informasi & Agenda")}
          </h1>
          <p className="text-sm text-slate-500 mt-1" data-testid="info-agenda-desc">
            {canViewAnn && canViewRem && "Pusat pengumuman, reminder, dan event dalam satu halaman."}
            {canViewAnn && !canViewRem && "Kelola pengumuman internal untuk seluruh anggota organisasi."}
            {!canViewAnn && canViewRem && "Kelola reminder dan event penting agar tidak terlewat."}
          </p>
        </div>
        {(canCreateAnn && canCreateRem) ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button data-testid="btn-buat" className="rounded-none bg-[#FF5A1F] hover:bg-[#e04a13] uppercase text-xs tracking-wider font-semibold h-9">
                <Plus size={13} className="mr-1" /> Buat <ChevronDown size={13} className="ml-1" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="rounded-none min-w-[220px]">
              <DropdownMenuItem data-testid="menu-buat-pengumuman" onClick={() => { switchTab("pengumuman"); setTimeout(() => { const b = document.querySelector('[data-testid="btn-new-announcement"]'); b?.click(); }, 300); }}>
                <Megaphone size={13} className="mr-2 text-[#FF5A1F]" /> Buat Pengumuman
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="menu-buat-reminder" onClick={() => { switchTab("reminder"); setTimeout(() => { const b = document.querySelector('[data-testid="btn-new-event"]'); b?.click(); }, 300); }}>
                <Bell size={13} className="mr-2 text-blue-600" /> Buat Reminder
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="menu-buat-event" onClick={() => { switchTab("reminder"); setTimeout(() => { const b = document.querySelector('[data-testid="btn-new-event"]'); b?.click(); }, 300); }}>
                <CalendarIcon size={13} className="mr-2 text-purple-600" /> Buat Event
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : canCreateAnn ? (
          <Button
            data-testid="btn-buat-pengumuman"
            onClick={() => { switchTab("pengumuman"); setTimeout(() => { const b = document.querySelector('[data-testid="btn-new-announcement"]'); b?.click(); }, 300); }}
            className="rounded-none bg-[#FF5A1F] hover:bg-[#e04a13] uppercase text-xs tracking-wider font-semibold h-9"
          >
            <Megaphone size={13} className="mr-1" /> Buat Pengumuman
          </Button>
        ) : canCreateRem ? (
          <Button
            data-testid="btn-buat-reminder"
            onClick={() => { switchTab("reminder"); setTimeout(() => { const b = document.querySelector('[data-testid="btn-new-event"]'); b?.click(); }, 300); }}
            className="rounded-none bg-[#FF5A1F] hover:bg-[#e04a13] uppercase text-xs tracking-wider font-semibold h-9"
          >
            <Bell size={13} className="mr-1" /> Buat Reminder
          </Button>
        ) : null}
      </div>

      {/* Tabs */}
      <div className="inline-flex border border-slate-300" data-testid="info-agenda-tabs">
        {availableTabs.map((t, i) => {
          const Icon = t.icon;
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              type="button"
              data-testid={`tab-${t.id}`}
              onClick={() => switchTab(t.id)}
              className={`px-4 py-2 text-xs uppercase tracking-wider font-semibold inline-flex items-center gap-1.5 ${i > 0 ? "border-l border-slate-300" : ""} ${active ? "bg-[#0B1D3A] text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
            >
              <Icon size={13} /> {t.label}
            </button>
          );
        })}
      </div>

      <StickyContextBar
        segments={["Informasi & Agenda", availableTabs.find((t) => t.id === tab)?.label || ""]}
        extra={<span className="text-slate-400 normal-case">{feed.length > 0 ? `${feed.length} item terkini` : ""}</span>}
      />

      {/* Tab content */}
      {tab === "ringkasan" && (
        <RingkasanTab kpi={kpi} feed={feed} kindBadge={KIND_BADGE} loading={loading}
          showAnn={canViewAnn} showRem={canViewRem}
          onOpen={(item) => {
            if (item._kind === "PENGUMUMAN") switchTab("pengumuman");
            else switchTab("reminder");
        }} />
      )}
      {tab === "pengumuman" && canViewAnn && (
        <div data-testid="tab-content-pengumuman" className="-mx-6 -mt-2">
          <AnnouncementsPage />
        </div>
      )}
      {tab === "reminder" && canViewRem && (
        <div data-testid="tab-content-reminder" className="-mx-6 -mt-2">
          <RemindersPage />
        </div>
      )}
      {tab === "kalender" && canViewRem && (
        <div data-testid="tab-content-kalender" className="-mx-6 -mt-2">
          <RemindersPage />
        </div>
      )}
      </>)}
    </div>
  );
}

function RingkasanTab({ kpi, feed, kindBadge, loading, onOpen, showAnn, showRem }) {
  return (
    <div className="space-y-4" data-testid="ringkasan-tab">
      {/* KPI Row. 149-cp: KPI adaptif — hanya tampilkan statistik untuk fitur yang
          dapat diakses. Pengumuman Aktif = feature announcements, sisanya = reminders. */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2" data-testid="ringkasan-kpi">
        {showAnn && (
          <KpiCard label="Pengumuman Aktif" value={kpi.annActive} tone="orange" icon={Megaphone} testid="kpi-ann-active" />
        )}
        {showRem && (
          <>
            <KpiCard label="Hari Ini"   value={kpi.hariIni}   tone="sky"     icon={CalendarIcon}  testid="kpi-today" />
            <KpiCard label="Minggu Ini" value={kpi.mingguIni} tone="slate"   icon={Clock}         testid="kpi-week" />
            <KpiCard label="Terlambat"  value={kpi.terlambat} tone="rose"    icon={AlertOctagon}  testid="kpi-overdue" />
            <KpiCard label="Selesai"    value={kpi.selesai}   tone="emerald" icon={CheckCircle2}  testid="kpi-done" />
          </>
        )}
      </div>

      {/* Unified feed */}
      <section className="bg-white border border-slate-200" data-testid="ringkasan-feed">
        <div className="px-4 py-2 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
          <div className="overline text-slate-600">Daftar Informasi & Agenda</div>
          <div className="text-[10px] text-slate-500 tabular-nums">{feed.length} item</div>
        </div>
        {loading && <div className="p-6 text-center text-xs text-slate-400 italic">Memuat…</div>}
        {!loading && feed.length === 0 && <div className="p-8 text-center text-xs text-slate-400 italic">Belum ada informasi atau agenda pada rentang ini.</div>}
        <ul className="divide-y divide-slate-100">
          {feed.slice(0, 30).map((item) => (
            <li key={item.id} data-testid={`feed-${item.id}`} onClick={() => onOpen(item)}
              className="flex items-start gap-3 px-4 py-2.5 hover:bg-slate-50 cursor-pointer">
              <span className={`px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider border shrink-0 ${kindBadge[item._kind] || "bg-slate-100 text-slate-700 border-slate-300"}`}>{item._kind}</span>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-[#0B1D3A] truncate" title={item.title}>
                  {item.title}
                  {item.distinguisher && (
                    <span className="ml-1 font-mono text-[10px] text-slate-400" data-testid="feed-distinguisher">{item.distinguisher}</span>
                  )}
                </div>
                <div className="text-[10px] text-slate-500 mt-0.5 truncate">
                  <span className="tabular-nums">{fmtDate(item._date)}</span> · {item.category} · PIC: {item.actor}
                </div>
              </div>
              <span className="text-[10px] font-semibold text-slate-500 whitespace-nowrap self-center">{item.status}</span>
            </li>
          ))}
        </ul>
      </section>

      <div className="text-[10px] text-slate-400 italic flex items-center gap-1"><Info size={10} /> Fase 1 rilis — drawer form baru & notifikasi cron akan menyusul di fase berikutnya.</div>
    </div>
  );
}

const TONE = {
  slate:   "bg-slate-50 border-slate-200 text-slate-700",
  orange:  "bg-orange-50 border-orange-200 text-orange-800",
  rose:    "bg-rose-50 border-rose-200 text-rose-800",
  emerald: "bg-emerald-50 border-emerald-200 text-emerald-800",
  sky:     "bg-sky-50 border-sky-200 text-sky-800",
};

function KpiCard({ label, value, tone, icon: Icon, testid }) {
  return (
    <div className={`border p-3 ${TONE[tone] || TONE.slate}`} data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="overline text-[10px]">{label}</span>
        {Icon && <Icon size={13} className="opacity-60" />}
      </div>
      <div className="font-display text-2xl tabular-nums mt-0.5">{value}</div>
    </div>
  );
}
